import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { marked } from "marked";
import TurndownService from "turndown";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered,
  Link as LinkIcon, Quote, Undo2, Redo2, ImagePlus, ImageUp, Loader2,
} from "lucide-react";

/**
 * Rich-text (WYSIWYG) editor whose value is MARKDOWN. The pipeline stays
 * markdown-based (parseMarkdownToSections + Portable Text), so we convert at the
 * edges: markdown → HTML (marked) on the way in, HTML → markdown (turndown) on
 * every edit. StarterKit (v3) includes the Link mark; we add Image so uploaded
 * pictures (`![alt](url)`) survive the round-trip and render inline. On publish,
 * a Sanity CDN image URL is turned into a real in-body image block.
 */

marked.setOptions({ gfm: true });

const turndown = new TurndownService({
  headingStyle: "atx",        // `## Heading` — what parseMarkdownToSections expects
  bulletListMarker: "-",
  codeBlockStyle: "fenced",
  emDelimiter: "*",
});

function mdToHtml(md: string): string {
  return marked.parse(md ?? "", { async: false }) as string;
}
function htmlToMd(html: string): string {
  return turndown.turndown(html).trim();
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 220,
  className,
  onUploadImage,
}: {
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  minHeight?: number;
  className?: string;
  /** Upload a picked image (data URL) → resolved public URL to insert, or null. When omitted, the upload button is hidden. */
  onUploadImage?: (dataUrl: string) => Promise<string | null>;
}) {
  // Track the last markdown we emitted so an external `value` change (e.g. "Run
  // links" replacing the body) re-seeds the editor, but our own keystrokes don't.
  const lastEmitted = useRef<string>(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      Image.configure({ inline: false }),
    ],
    content: mdToHtml(value),
    editorProps: {
      attributes: {
        dir: "auto",
        class:
          "min-h-[inherit] outline-none " +
          "[&_h2]:mt-3 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:font-semibold " +
          "[&_ul]:list-disc [&_ul]:pr-5 [&_ol]:list-decimal [&_ol]:pr-5 [&_li]:my-0.5 " +
          "[&_a]:text-brand [&_a]:underline [&_blockquote]:border-r-2 [&_blockquote]:border-line [&_blockquote]:pr-3 [&_blockquote]:text-muted " +
          "[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-md [&_img]:border [&_img]:border-line " +
          "[&_p]:my-1.5 leading-relaxed",
      },
    },
    onUpdate: ({ editor }) => {
      const md = htmlToMd(editor.getHTML());
      lastEmitted.current = md;
      onChange(md);
    },
  });

  useEffect(() => {
    if (editor && value !== lastEmitted.current) {
      lastEmitted.current = value;
      editor.commands.setContent(mdToHtml(value), { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className={`rounded-lg border border-line bg-surface ${className ?? ""}`}>
      <Toolbar editor={editor} onUploadImage={onUploadImage} />
      <div className="relative px-3 py-2 text-sm" style={{ minHeight }}>
        {value.trim() === "" && placeholder && (
          <p className="pointer-events-none absolute inset-x-3 top-3 truncate text-muted" aria-hidden>
            {placeholder}
          </p>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor, onUploadImage }: { editor: Editor; onUploadImage?: (dataUrl: string) => Promise<string | null> }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const btn = (active: boolean) =>
    `rounded p-1.5 hover:bg-line ${active ? "bg-line text-brand" : "text-muted"}`;

  const setLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", prev ?? "https://");
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const insertImageMarker = () => {
    const desc = window.prompt("Describe the image to GENERATE here (AI will create it)", "");
    if (desc === null) return;
    editor.chain().focus().insertContent(`{{IMAGE: ${desc.trim()}}}`).run();
  };

  // Upload a real picture the operator picks → public URL → inline image node.
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !onUploadImage) return;
    setUploading(true);
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result as string);
        r.onerror = () => rej(new Error("could not read the file"));
        r.readAsDataURL(file);
      });
      const url = await onUploadImage(dataUrl);
      if (url) {
        const alt = window.prompt("Image description (alt text — for SEO + accessibility)", "") ?? "";
        editor.chain().focus().setImage({ src: url, alt }).run();
      }
    } catch (err) {
      window.alert(`Image upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-line px-2 py-1">
      <button type="button" title="Bold" className={btn(editor.isActive("bold"))} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></button>
      <button type="button" title="Italic" className={btn(editor.isActive("italic"))} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></button>
      <span className="mx-1 h-4 w-px bg-line" />
      <button type="button" title="Heading" className={btn(editor.isActive("heading", { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></button>
      <button type="button" title="Subheading" className={btn(editor.isActive("heading", { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></button>
      <span className="mx-1 h-4 w-px bg-line" />
      <button type="button" title="Bulleted list" className={btn(editor.isActive("bulletList"))} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></button>
      <button type="button" title="Numbered list" className={btn(editor.isActive("orderedList"))} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></button>
      <button type="button" title="Quote" className={btn(editor.isActive("blockquote"))} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></button>
      <span className="mx-1 h-4 w-px bg-line" />
      <button type="button" title="Link" className={btn(editor.isActive("link"))} onClick={setLink}><LinkIcon className="h-4 w-4" /></button>
      {onUploadImage && (
        <>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} />
          <button type="button" title="Upload an image into the article (renders in-body)" className={btn(false)} disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />}
          </button>
        </>
      )}
      <button type="button" title="Insert an image SPOT for AI to generate ({{IMAGE:…}})" className={btn(false)} onClick={insertImageMarker}><ImagePlus className="h-4 w-4" /></button>
      <span className="mx-1 h-4 w-px bg-line" />
      <button type="button" title="Undo" className={btn(false)} onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-4 w-4" /></button>
      <button type="button" title="Redo" className={btn(false)} onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-4 w-4" /></button>
    </div>
  );
}
